// =============================================================================
// FILE: packages/renderer/src/stores/vault.store.ts
// PURPOSE: Manages R2 Project Vault state in the frontend.
//   - Detects whether the desktop vault API is available (window.r2Vault)
//   - Persists vault config loaded from the main process
//   - Tracks active copy operations and watched projects
// =============================================================================

import { useSyncExternalStore, useEffect } from 'react';

// ── Type declarations for window.r2Vault ──────────────────────────────────────
export interface VaultConfig {
  vaultPath:    string | null;
  autoReindex:  boolean;
  watchers:     string[];
}

export interface CopyProgress {
  copied:      number;
  total:       number;
  currentFile: string;
  pct:         number;
}

export interface VaultProject {
  projectNumber: string;
  manifest:      Record<string, unknown> | null;
  watching:      boolean;
}

declare global {
  interface Window {
    r2Vault?: {
      selectFolder:         ()                       => Promise<string | null>;
      selectIngestItems?:   ()                       => Promise<unknown | null>;
      selectVaultLocation:  ()                       => Promise<string | null>;
      getConfig:            ()                       => Promise<VaultConfig>;
      saveConfig:           (u: Partial<VaultConfig>)=> Promise<VaultConfig>;
      scanFolder:           (p: string)              => Promise<unknown[]>;
      copyToVault:          (opts: { sourcePath: string; projectNumber: string; vaultPath: string }, onProgress?: (p: CopyProgress) => void) => Promise<{ success: boolean; copied: number; total: number; errors: unknown[]; projDir?: string }>;
      watchStart:           (pn: string, vp: string, cb: (e: unknown) => void) => Promise<{ ok: boolean; stop?: () => Promise<unknown> }>;
      watchStop:            (pn: string)             => Promise<{ ok: boolean }>;
      listProjects:         (vp: string)             => Promise<VaultProject[]>;
      openInExplorer:       (p: string)              => Promise<void>;
      exportProject:        (opts: { projectNumber: string; vaultPath: string }) => Promise<unknown>;
    };
  }
}

// ── Store state ────────────────────────────────────────────────────────────────
export interface VaultState {
  available:  boolean;       // window.r2Vault is present
  config:     VaultConfig;
  projects:   VaultProject[];
  loading:    boolean;
}

const DEFAULT_CONFIG: VaultConfig = { vaultPath: null, autoReindex: true, watchers: [] };
const DEFAULTS: VaultState = { available: false, config: DEFAULT_CONFIG, projects: [], loading: false };

const subs = new Set<() => void>();
let state: VaultState = { ...DEFAULTS, available: typeof window !== 'undefined' && !!window.r2Vault };
const notify = (): void => { for (const fn of subs) fn(); };

export const vaultStore = {
  getState: (): VaultState => state,

  async init(): Promise<void> {
    if (!window.r2Vault) return;
    state = { ...state, loading: true }; notify();
    const config   = await window.r2Vault.getConfig();
    const projects = config.vaultPath
      ? (await window.r2Vault.listProjects(config.vaultPath) as VaultProject[])
      : [];
    state = { ...state, config, projects, loading: false }; notify();
  },

  async setupVault(): Promise<string | null> {
    if (!window.r2Vault) return null;
    const vaultPath = await window.r2Vault.selectVaultLocation();
    if (!vaultPath) return null;
    const config = await window.r2Vault.saveConfig({ vaultPath });
    state = { ...state, config }; notify();
    return vaultPath;
  },

  setConfig(config: VaultConfig): void {
    state = { ...state, config }; notify();
  },

  setProjects(projects: VaultProject[]): void {
    state = { ...state, projects }; notify();
  },

  subscribe: (fn: () => void): (() => void) => { subs.add(fn); return () => subs.delete(fn); },
};

export function useVaultStore(): VaultState {
  return useSyncExternalStore(vaultStore.subscribe, vaultStore.getState, vaultStore.getState);
}

/** Auto-initialise when running in the desktop app */
export function useVaultInit(): void {
  useEffect(() => {
    if (window.r2Vault) void vaultStore.init();
  }, []);
}
